/**
 * ReplayViewer UI Component
 * Modal viewer for replaying past games with playback controls
 */

import { gameHistoryService, type GameWithPlayers, type PlayerInfo } from '../../history';
import type { MoveRecord, Point } from '../../game/types';

export interface ReplayViewerOptions {
  container: HTMLElement;
  onClose?: () => void;
}

interface ReplayRenderState {
  players: {
    1: { pos: Point | null; path: Point[] };
    2: { pos: Point | null; path: Point[] };
  };
  points: Array<{ x: number; y: number; player: 1 | 2 }>;
}

export class ReplayViewer {
  private container: HTMLElement;
  private onClose?: () => void;
  private game: GameWithPlayers | null = null;
  private currentMoveIndex: number = 0;
  private isPlaying: boolean = false;
  private playbackSpeed: number = 1;
  private playbackInterval: number | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;

  // Render config
  private readonly padding = 30;
  private cellSize = 60;
  private readonly colors = {
    background: '#2a2a4a',
    gridLines: '#444466',
    labels: '#666',
    player1: '#e74c3c',
    player2: '#3498db',
    highlight: '#2ecc71',
  };

  constructor(options: ReplayViewerOptions) {
    this.container = options.container;
    this.onClose = options.onClose;
  }

  /**
   * Open replay viewer with a specific game ID
   */
  async openGame(gameId: string): Promise<void> {
    const game = await gameHistoryService.getGame(gameId);
    if (!game) {
      console.error('Game not found:', gameId);
      return;
    }

    this.game = game;
    this.currentMoveIndex = 0;
    this.isPlaying = false;
    this.playbackSpeed = 1;

    this.render();
    this.setupCanvas();
    this.drawCurrentState();
  }

  /**
   * Open replay viewer with game data directly
   * Starts at the final state (all moves shown)
   */
  openWithData(game: GameWithPlayers): void {
    this.game = game;
    // Start at final state (show all moves)
    this.currentMoveIndex = game.moveHistory?.length || 0;
    this.isPlaying = false;
    this.playbackSpeed = 1;

    this.render();
    this.setupCanvas();
    this.drawCurrentState();
  }

  private render(): void {
    if (!this.game) return;

    const resultInfo = this.getResultInfo();
    const player1Info = this.formatPlayerInfo(this.game.player1, 1);
    const player2Info = this.formatPlayerInfo(this.game.player2, 2);
    const shareUrl = gameHistoryService.generateShareLink(this.game.id);

    this.container.innerHTML = `
      <div class="replay-overlay">
        <div class="replay-modal">
          <div class="replay-header">
            <h2 class="replay-title">Game Replay</h2>
            <button class="replay-close-btn" aria-label="Close">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <div class="replay-players">
            <div class="replay-player player1 ${resultInfo.winner === 1 ? 'winner' : ''}">
              ${player1Info.avatar}
              <span class="replay-player-name">${this.escapeHtml(player1Info.name)}</span>
              ${resultInfo.winner === 1 ? '<span class="winner-badge">Winner</span>' : ''}
            </div>
            <span class="replay-vs">vs</span>
            <div class="replay-player player2 ${resultInfo.winner === 2 ? 'winner' : ''}">
              ${player2Info.avatar}
              <span class="replay-player-name">${this.escapeHtml(player2Info.name)}</span>
              ${resultInfo.winner === 2 ? '<span class="winner-badge">Winner</span>' : ''}
            </div>
          </div>

          <div class="replay-canvas-container">
            <canvas class="replay-canvas"></canvas>
          </div>

          <div class="replay-info">
            <span class="replay-move-counter">Move <span class="current-move">0</span> / <span class="total-moves">${this.game.moveHistory?.length || 0}</span></span>
            <span class="replay-board-size">${this.game.boardSize}x${this.game.boardSize} board</span>
          </div>

          <div class="replay-controls">
            <button class="replay-btn" data-action="first" title="First move">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M11 19l-7-7 7-7M19 19l-7-7 7-7"/>
              </svg>
            </button>
            <button class="replay-btn" data-action="prev" title="Previous move">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M15 18l-6-6 6-6"/>
              </svg>
            </button>
            <button class="replay-btn play-pause" data-action="play" title="Play/Pause">
              <svg class="play-icon" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 5v14l11-7z"/>
              </svg>
              <svg class="pause-icon" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" style="display: none;">
                <path d="M6 4h4v16H6zM14 4h4v16h-4z"/>
              </svg>
            </button>
            <button class="replay-btn" data-action="next" title="Next move">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M9 18l6-6-6-6"/>
              </svg>
            </button>
            <button class="replay-btn" data-action="last" title="Last move">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M13 5l7 7-7 7M5 5l7 7-7 7"/>
              </svg>
            </button>
          </div>

          <div class="replay-speed">
            <label>Speed:</label>
            <select class="replay-speed-select">
              <option value="0.5">0.5x</option>
              <option value="1" selected>1x</option>
              <option value="2">2x</option>
            </select>
          </div>

          <div class="replay-actions">
            <button class="replay-share-btn">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="18" cy="5" r="3"/>
                <circle cx="6" cy="12" r="3"/>
                <circle cx="18" cy="19" r="3"/>
                <path d="M8.59 13.51l6.83 3.98M15.41 6.51l-6.82 3.98"/>
              </svg>
              Share
            </button>
          </div>

          <input type="text" class="replay-share-input" value="${shareUrl}" readonly style="display: none;">
        </div>
      </div>
    `;

    this.attachEventListeners();
  }

  private attachEventListeners(): void {
    // Close button
    const closeBtn = this.container.querySelector('.replay-close-btn');
    closeBtn?.addEventListener('click', () => this.close());

    // Overlay click to close
    const overlay = this.container.querySelector('.replay-overlay');
    overlay?.addEventListener('click', (e) => {
      if (e.target === overlay) this.close();
    });

    // Control buttons
    const controls = this.container.querySelectorAll('.replay-btn');
    controls.forEach(btn => {
      btn.addEventListener('click', () => {
        const action = btn.getAttribute('data-action');
        switch (action) {
          case 'first':
            this.goToMove(0);
            break;
          case 'prev':
            this.goToMove(this.currentMoveIndex - 1);
            break;
          case 'play':
            this.togglePlay();
            break;
          case 'next':
            this.goToMove(this.currentMoveIndex + 1);
            break;
          case 'last':
            this.goToMove(this.game?.moveHistory?.length || 0);
            break;
        }
      });
    });

    // Speed select
    const speedSelect = this.container.querySelector('.replay-speed-select') as HTMLSelectElement;
    speedSelect?.addEventListener('change', () => {
      this.playbackSpeed = parseFloat(speedSelect.value);
      if (this.isPlaying) {
        this.stopPlayback();
        this.startPlayback();
      }
    });

    // Share button
    const shareBtn = this.container.querySelector('.replay-share-btn');
    const shareInput = this.container.querySelector('.replay-share-input') as HTMLInputElement;
    shareBtn?.addEventListener('click', () => {
      shareInput.style.display = shareInput.style.display === 'none' ? 'block' : 'none';
      if (shareInput.style.display === 'block') {
        shareInput.select();
        navigator.clipboard?.writeText(shareInput.value).then(() => {
          shareBtn.textContent = 'Copied!';
          setTimeout(() => {
            if (shareBtn) {
              shareBtn.innerHTML = `
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="18" cy="5" r="3"/>
                  <circle cx="6" cy="12" r="3"/>
                  <circle cx="18" cy="19" r="3"/>
                  <path d="M8.59 13.51l6.83 3.98M15.41 6.51l-6.82 3.98"/>
                </svg>
                Share
              `;
            }
          }, 2000);
        });
      }
    });

    // Keyboard shortcuts
    document.addEventListener('keydown', this.handleKeydown);
  }

  private handleKeydown = (e: KeyboardEvent): void => {
    if (!this.game) return;

    switch (e.key) {
      case 'Escape':
        this.close();
        break;
      case 'ArrowLeft':
        this.goToMove(this.currentMoveIndex - 1);
        break;
      case 'ArrowRight':
        this.goToMove(this.currentMoveIndex + 1);
        break;
      case ' ':
        e.preventDefault();
        this.togglePlay();
        break;
      case 'Home':
        this.goToMove(0);
        break;
      case 'End':
        this.goToMove(this.game.moveHistory?.length || 0);
        break;
    }
  };

  private setupCanvas(): void {
    if (!this.game) return;

    this.canvas = this.container.querySelector('.replay-canvas') as HTMLCanvasElement;
    if (!this.canvas) return;

    this.ctx = this.canvas.getContext('2d');
    if (!this.ctx) return;

    // Calculate cell size based on board size
    const gridSize = this.game.boardSize;
    if (gridSize <= 5) {
      this.cellSize = 70;
    } else if (gridSize <= 7) {
      this.cellSize = 55;
    } else {
      this.cellSize = 45;
    }

    const canvasSize = this.padding * 2 + (gridSize - 1) * this.cellSize;
    this.canvas.width = canvasSize;
    this.canvas.height = canvasSize;
  }

  private goToMove(moveIndex: number): void {
    if (!this.game) return;

    const maxMove = this.game.moveHistory?.length || 0;
    this.currentMoveIndex = Math.max(0, Math.min(moveIndex, maxMove));
    this.updateMoveCounter();
    this.drawCurrentState();
  }

  private togglePlay(): void {
    if (this.isPlaying) {
      this.stopPlayback();
    } else {
      this.startPlayback();
    }
  }

  private startPlayback(): void {
    if (!this.game) return;

    const maxMove = this.game.moveHistory?.length || 0;
    if (this.currentMoveIndex >= maxMove) {
      this.currentMoveIndex = 0;
    }

    this.isPlaying = true;
    this.updatePlayPauseButton();

    const intervalMs = 1000 / this.playbackSpeed;
    this.playbackInterval = window.setInterval(() => {
      const maxMove = this.game?.moveHistory?.length || 0;
      if (this.currentMoveIndex >= maxMove) {
        this.stopPlayback();
        return;
      }

      this.currentMoveIndex++;
      this.updateMoveCounter();
      this.drawCurrentState();
    }, intervalMs);
  }

  private stopPlayback(): void {
    this.isPlaying = false;
    this.updatePlayPauseButton();

    if (this.playbackInterval !== null) {
      clearInterval(this.playbackInterval);
      this.playbackInterval = null;
    }
  }

  private updatePlayPauseButton(): void {
    const playIcon = this.container.querySelector('.play-icon') as HTMLElement;
    const pauseIcon = this.container.querySelector('.pause-icon') as HTMLElement;

    if (playIcon && pauseIcon) {
      playIcon.style.display = this.isPlaying ? 'none' : 'block';
      pauseIcon.style.display = this.isPlaying ? 'block' : 'none';
    }
  }

  private updateMoveCounter(): void {
    const currentMoveEl = this.container.querySelector('.current-move');
    if (currentMoveEl) {
      currentMoveEl.textContent = String(this.currentMoveIndex);
    }
  }

  private buildStateAtMove(moveIndex: number): ReplayRenderState {
    const state: ReplayRenderState = {
      players: {
        1: { pos: null, path: [] },
        2: { pos: null, path: [] },
      },
      points: [],
    };

    if (!this.game?.moveHistory) return state;

    const moves = this.game.moveHistory.slice(0, moveIndex);

    for (const move of moves) {
      const player = move.player;

      if (move.isFirstMove) {
        state.points.push({ x: move.from.x, y: move.from.y, player });
        state.points.push({ x: move.to.x, y: move.to.y, player });
        state.players[player].path.push({ ...move.from });
        state.players[player].path.push({ ...move.to });
        state.players[player].pos = { ...move.to };
      } else {
        state.points.push({ x: move.to.x, y: move.to.y, player });
        state.players[player].path.push({ ...move.to });
        state.players[player].pos = { ...move.to };
      }
    }

    return state;
  }

  private drawCurrentState(): void {
    if (!this.canvas || !this.ctx || !this.game) return;

    const state = this.buildStateAtMove(this.currentMoveIndex);
    const lastMove = this.currentMoveIndex > 0 ? this.game.moveHistory?.[this.currentMoveIndex - 1] : null;
    const gridSize = this.game.boardSize;

    const ctx = this.ctx;

    // Clear and draw background
    ctx.fillStyle = this.colors.background;
    ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

    // Draw grid lines
    ctx.strokeStyle = this.colors.gridLines;
    ctx.lineWidth = 1;

    for (let i = 0; i < gridSize; i++) {
      ctx.beginPath();
      ctx.moveTo(this.padding + i * this.cellSize, this.padding);
      ctx.lineTo(this.padding + i * this.cellSize, this.padding + (gridSize - 1) * this.cellSize);
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(this.padding, this.padding + i * this.cellSize);
      ctx.lineTo(this.padding + (gridSize - 1) * this.cellSize, this.padding + i * this.cellSize);
      ctx.stroke();
    }

    // Draw coordinate labels
    ctx.fillStyle = this.colors.labels;
    ctx.font = '12px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    for (let i = 0; i < gridSize; i++) {
      ctx.fillText(
        String.fromCharCode(65 + i),
        this.padding + i * this.cellSize,
        this.padding + (gridSize - 1) * this.cellSize + 20
      );
      ctx.fillText(
        (gridSize - i).toString(),
        this.padding - 15,
        this.padding + i * this.cellSize
      );
    }

    // Draw player paths
    ctx.lineWidth = 4;
    ctx.lineCap = 'round';

    ctx.strokeStyle = this.colors.player1;
    this.drawPath(state.players[1].path, gridSize);

    ctx.strokeStyle = this.colors.player2;
    this.drawPath(state.players[2].path, gridSize);

    // Draw all points
    for (const point of state.points) {
      const pos = this.gridToPixel(point.x, point.y, gridSize);
      ctx.beginPath();
      ctx.arc(pos.x, pos.y, 10, 0, Math.PI * 2);
      ctx.fillStyle = point.player === 1 ? this.colors.player1 : this.colors.player2;
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 2;
      ctx.stroke();
    }

    // Highlight last move
    if (lastMove) {
      const toPos = this.gridToPixel(lastMove.to.x, lastMove.to.y, gridSize);
      ctx.beginPath();
      ctx.arc(toPos.x, toPos.y, 16, 0, Math.PI * 2);
      ctx.strokeStyle = this.colors.highlight;
      ctx.lineWidth = 3;
      ctx.stroke();
    }
  }

  private drawPath(path: Point[], gridSize: number): void {
    if (!this.ctx || path.length < 2) return;

    this.ctx.beginPath();
    const start = this.gridToPixel(path[0].x, path[0].y, gridSize);
    this.ctx.moveTo(start.x, start.y);

    for (let i = 1; i < path.length; i++) {
      const point = this.gridToPixel(path[i].x, path[i].y, gridSize);
      this.ctx.lineTo(point.x, point.y);
    }
    this.ctx.stroke();
  }

  private gridToPixel(gx: number, gy: number, gridSize: number): { x: number; y: number } {
    return {
      x: this.padding + gx * this.cellSize,
      y: this.padding + (gridSize - 1 - gy) * this.cellSize,
    };
  }

  private getResultInfo(): { winner: 1 | 2 | null } {
    if (!this.game) return { winner: null };
    
    if (!this.game.winnerId) return { winner: null };
    if (this.game.winnerId === this.game.player1Id) return { winner: 1 };
    if (this.game.winnerId === this.game.player2Id) return { winner: 2 };
    return { winner: null };
  }

  private formatPlayerInfo(player: PlayerInfo | null, playerNum: 1 | 2): { name: string; avatar: string } {
    if (!player) {
      const name = this.game?.gameType === 'vs_ai' && playerNum === 2 ? 'AI' : `Player ${playerNum}`;
      const initial = name[0].toUpperCase();
      return {
        name,
        avatar: `<div class="replay-avatar-placeholder">${initial}</div>`,
      };
    }

    const name = player.displayName || player.username;
    const avatar = player.avatarUrl
      ? `<img class="replay-avatar" src="${player.avatarUrl}" alt="">`
      : `<div class="replay-avatar-placeholder">${name[0].toUpperCase()}</div>`;

    return { name, avatar };
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Close the replay viewer
   */
  close(): void {
    // Prevent double-close
    if (!this.game && this.container.innerHTML === '') return;
    
    this.stopPlayback();
    document.removeEventListener('keydown', this.handleKeydown);
    this.container.innerHTML = '';
    this.game = null;
    
    // Call onClose and clear it to prevent infinite recursion
    const callback = this.onClose;
    this.onClose = undefined;
    callback?.();
  }

  /**
   * Check if viewer is currently open
   */
  isOpen(): boolean {
    return this.game !== null;
  }

  /**
   * Destroy the component
   */
  destroy(): void {
    this.close();
  }
}
